import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import SessionStorage from '../../../helpers/storage/SessionStorage';

/**
 * FocusManager
 *
 * This component improves accessibility and makes navigating a single page app
 * more like navigating traditional web pages by restoring scroll position and
 * setting the focus on the page contents.
 *
 * The component is intended to be wrapped around the app's different routes
 * views (likely in your App container) similar to this:
 *
 *     <Page>
 *         <Header />
 *         <FocusManager>
 *             <MainContents />
 *         </FocusManager>
 *         <Footer />
 *     </Page>
 *
 * FocusManager performs two main tasks:
 * Before a route change, the clicked element id and current scroll position are
 * saved in sessionStorage. If the clicked item does not have an id, then only
 * the scroll position is saved.
 *
 * After a route change, it checks for a saved information about the current
 * route. If a saved scroll position is found, it sets it. Focus is then set
 * based on availability in this order:
 *   1. A saved link or button id
 *   2. The first h1 on the page
 *   3. The wrapping div created by FocusManager
 *
 * Focus outlines for the headline and wrapping div are hidden so sighted users
 * are not distracted by the "unnatural" focus. This does not affect a screen
 * reader's focus highlight.
 *
 *
 * Caveats
 * If a link or button has an id, and also has a child element such as a span,
 * the click target might be that child element, preventing the parent's id
 * from being saved. You can get around this by applying the CSS rule
 * `pointer-event: none` to any children.
 */

export default class FocusManager extends React.PureComponent {
    static propTypes = {
        location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
        children: PropTypes.node.isRequired
    };

    constructor(props) {
        super(props);

        this.wrapperRef = React.createRef();

        this.scrollManagedPaths = {};
        this.focusManagedPaths = {};
        this.hasSetFocus = false;
        this.attempts = 0;
    }

    componentDidMount() {
        this.getManagedPaths();

        // Add event listeners
        document.addEventListener('mousedown', this.saveFocusAndScroll);
        document.addEventListener('keydown', this.saveFocusAndScroll);

        this.attemptToSetFocus();
    }

    componentDidUpdate(prevProps) {
        const { location } = this.props;

        // If the change is inside the same micro service, we can watch for location changes
        if (location.pathname !== prevProps.location.pathname && location.hash.length === 0) {
            this.hasSetFocus = false;
            this.attemptToSetFocus();
        }

        // Because we can't watch for location changes when switching between
        // micro services, so we're using the flag `hasSetFocus` to know if we've
        // already set the focus -- because we only want to do it once.
        // We're also checking that the user isn't trying to go to an internal anchor
        else if (!this.hasSetFocus && location.hash.length === 0) {
            this.attemptToSetFocus();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.saveFocusAndScroll);
        document.removeEventListener('keydown', this.saveFocusAndScroll);
    }

    getManagedPaths() {
        if (!SessionStorage) {
            return;
        }

        this.scrollManagedPaths = JSON.parse(SessionStorage.getItem("scrollManagedPaths")) || {};
        this.focusManagedPaths = JSON.parse(SessionStorage.getItem("focusManagedPaths")) || {};
    }

    setManagedPaths() {
        if (SessionStorage) {
            SessionStorage.setItem("scrollManagedPaths", JSON.stringify(this.scrollManagedPaths));
            SessionStorage.setItem("focusManagedPaths", JSON.stringify(this.focusManagedPaths));
        }
    }

    setFocus() {
        const { location } = this.props;
        const validFocusableItems = ["A", "BUTTON"];
        const firstHeading = document.getElementsByTagName("h1")[0];

        const storedId = this.focusManagedPaths[location.pathname] || null;
        let hasFocusableId = false;

        // Set focus to storedId, then firstHeading, and then the wrapper
        if (storedId) {
            try {
                const tag = document.getElementById(storedId).tagName.toUpperCase();
                hasFocusableId = validFocusableItems.includes(tag);
            }
            catch (e) {
                // If there's a stored id but we can't find it, we exit out of
                // this method and wait for didUpdate to get called again ...
                // maybe the element will show up next time 🤞
                this.hasSetFocus = false;
                return;
            }
        }

        if (hasFocusableId) {
            const itemToBeFocused = document.getElementById(storedId);

            if (itemToBeFocused) {
                try {
                    setTimeout(() => {
                        document.getElementById(storedId).focus();
                    }, 0);
                }
                catch (e) {
                    console.warn(`Failed to focus ${itemToBeFocused}`);
                }
            }
        }
        else if (firstHeading && firstHeading.focus) {
            firstHeading.setAttribute("tabIndex", -1);
            firstHeading.style.outline = "none";

            try {
                setTimeout(() => {
                    firstHeading.focus();
                }, 0);
            }
            catch (e) {
                console.warn('Failed to focus h1');
            }
        }
        else if (this.wrapperRef.current.focus) {
            try {
                setTimeout(() => {
                    this.wrapperRef.current.focus();
                }, 0);
            }
            catch (e) {
                console.warn('Failed to focus wrapperRef');
            }
        }

        this.hasSetFocus = true;

        // Delete the stored id so it's not reused
        if (storedId) {
            delete this.focusManagedPaths[location.pathname];
            this.setManagedPaths();
        }
    }

    setScroll() {
        const { location } = this.props;
        const savedScrollPosition = this.scrollManagedPaths[location.pathname];

        if (savedScrollPosition) {
            // Set scroll position to saved value
            window.scroll(0, savedScrollPosition);
            // Delete the saved value so it's not reused
            delete this.scrollManagedPaths[location.pathname];
            this.setManagedPaths();
        }
        else {
            // No saved scroll position, so scroll to the top
            window.scroll(0, 0);
        }
    }

    saveFocusAndScroll = (e) => {
        const id = e.target.id || null;
        const { location } = this.props;

        // Save clicked id
        this.focusManagedPaths[location.pathname] = id;

        // Save scroll position
        const scrollPosition = window.scrollY || window.scrollTop || document.getElementsByTagName("html")[0].scrollTop || 0;

        this.scrollManagedPaths[location.pathname] = scrollPosition;

        // Commit to sessionStorage
        this.setManagedPaths();
    };

    attemptToSetFocus = debounce(() => {
        this.setFocus();
        this.setScroll();
    }, 300, { trailing: true, leading: false });

    render() {
        const { children } = this.props;

        return (
            <div data-component="FocusManager" ref={this.wrapperRef} tabIndex="-1" style={{ outline: "none" }}>
                {children}
            </div>
        );
    }
}

export function clearManagedPaths() {
    if (SessionStorage) {
        SessionStorage.removeItem("scrollManagedPaths");
        SessionStorage.removeItem("focusManagedPaths");
    }
}
